import { objectSpread2 as _objectSpread2, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';
import styles from './CarouselControl.module.scss.js';
import { Navigation } from './Navigation.js';
import { useResizeObserver, useIntersectionObserver, useWindowResizeListener, getCssBreakpoints, breakpointMapping, isTruthy, getCurrentPageSize, getIsPeek, getItemWidth, getPeekWidth, getScrollMargin, setRootCssVariable, getBreakpointName, wrapperScrollTo, getLabelText, makeArrowButtonOnClick, getScrollSnapAlign, getIsScrollable } from './utils.js';

var CarouselControl = props => {
  var wrapperRef = React.useRef(null);
  var itemsRef = React.useRef([]);
  var [itemsRefState, setItemsRefState] = React.useState([]);
  var [breakpointName, setBreakpointName] = React.useState('xs');
  var [itemWidth, setItemWidth] = React.useState(0);
  var [isNavigationVisible, setIsNavigationVisible] = React.useState(true);
  var wrapperSize = useResizeObserver(wrapperRef.current);
  var itemsVisibility = useIntersectionObserver(wrapperRef.current, itemsRefState);
  var windowSize = useWindowResizeListener(window);
  var {
    activeSlide = 0,
    children,
    pageSize,
    onChange,
    id,
    className
  } = props;
  var pageSizeWithDefaults = React.useMemo(() => _objectSpread2(_objectSpread2({}, {
    xs: 1,
    sm: 1,
    md: 2,
    lg: 2,
    xl: 3
  }), pageSize), [pageSize]);
  React.useEffect(() => {
    var cssBreakpoints = getCssBreakpoints();
    Object.keys(cssBreakpoints).forEach(cssBreakpointName => {
      breakpointMapping[cssBreakpointName].width =
      // @ts-ignore
      cssBreakpoints[cssBreakpointName];
    });
  }, []);
  React.useLayoutEffect(() => {
    itemsRef.current = itemsRef.current.filter(isTruthy);
    setItemsRefState(itemsRef.current);
  }, [children]);
  React.useEffect(() => {
    var itemCount = React.Children.count(children);
    var currentPageSize = getCurrentPageSize(
    // @ts-ignore
    pageSizeWithDefaults[breakpointName], itemCount);
    var scrollSnapAlign = getScrollSnapAlign(currentPageSize);
    var isScrollable = getIsScrollable(currentPageSize, itemCount);
    var isPeek = getIsPeek(isScrollable, breakpointMapping[breakpointName].peek);

    // eslint-disable-next-line no-underscore-dangle
    var _itemWidth = getItemWidth(wrapperSize.width, breakpointMapping[breakpointName].itemMarginLeft, currentPageSize, isPeek);
    var peekWidth = getPeekWidth(wrapperSize.width, breakpointMapping[breakpointName].itemMarginLeft, currentPageSize, _itemWidth);
    var scrollMarginInPx = getScrollMargin(scrollSnapAlign, breakpointMapping[breakpointName].itemMarginLeft, peekWidth, isPeek);
    setItemWidth(_itemWidth);
    setIsNavigationVisible(isScrollable);
    setRootCssVariable('--carousel-horizontal-margin', "".concat(breakpointMapping[breakpointName].itemMarginLeft, "px"));
    setRootCssVariable('--carousel-scroll-margin-left', "".concat(scrollMarginInPx, "px"));
    setRootCssVariable('--carousel-scroll-snap-align', scrollSnapAlign);
    setRootCssVariable('--carousel-item-width', "".concat(Math.floor(_itemWidth), "px"));
  }, [breakpointName, pageSizeWithDefaults, children, wrapperSize.width]);
  React.useEffect(() => setBreakpointName(getBreakpointName(breakpointMapping, windowSize.width)), [windowSize]);
  React.useEffect(() => {
    if (typeof onChange === 'function') {
      onChange(itemsVisibility);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsVisibility, onChange]);
  React.useEffect(() => {
    if (activeSlide < React.Children.count(children) && itemWidth > 0) {
      var left = itemWidth * activeSlide + breakpointMapping[breakpointName].itemMarginLeft * 2 * activeSlide;
      wrapperScrollTo(left, wrapperRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlide, itemWidth]);
  var hcid = "".concat(CarouselControl.name.toLocaleLowerCase(), "-").concat("10.6.2");
  return /*#__PURE__*/React.createElement("div", _extends({}, _objectSpread2({
    className: classNames(styles.container, className)
  }, id && {
    id: "".concat(id, "-carousel")
  }), {
    "data-hcid": hcid
  }), /*#__PURE__*/React.createElement("ul", {
    className: styles.wrapper,
    ref: wrapperRef
  }, React.Children.map(children, (child, index) =>
  /*#__PURE__*/
  // eslint-disable-line no-return-assign
  React.createElement("li", {
    ref: el => itemsRef.current[index] = el // eslint-disable-line
    ,
    key: index // eslint-disable-line
    ,
    className: styles.item
  }, child))), /*#__PURE__*/React.createElement(CSSTransition, {
    in: isNavigationVisible,
    appear: isNavigationVisible,
    timeout: 400,
    mountOnEnter: true,
    unmountOnExit: true,
    classNames: {
      enter: styles['navigation-enter'],
      enterActive: styles['navigation-enter-active'],
      exit: styles['navigation-exit'],
      exitActive: styles['navigation-exit-active']
    }
  }, /*#__PURE__*/React.createElement(Navigation, {
    labelText: getLabelText(itemsVisibility),
    previousDisabled: itemsVisibility[0],
    nextDisabled: itemsVisibility[itemsVisibility.length - 1],
    previousOnClick: makeArrowButtonOnClick(-itemWidth / 2, wrapperRef.current),
    nextOnClick: makeArrowButtonOnClick(itemWidth / 2, wrapperRef.current)
  })));
};
CarouselControl.propTypes = {
  activeSlide: PropTypes.number,
  children: PropTypes.node,
  className: PropTypes.string,
  onChange: PropTypes.func,
  pageSize: PropTypes.shape({
    lg: PropTypes.oneOf([2, 3]),
    md: PropTypes.oneOf([2]),
    sm: PropTypes.oneOf([1]),
    xl: PropTypes.oneOf([2, 3, 4]),
    xs: PropTypes.oneOf([1])
  })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
};

CarouselControl.displayName = 'CarouselControl';

export { CarouselControl };
